import React, { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import { IconButton } from '@mui/material';
import { get, set, size } from 'global/lib/lodash';
import { FullScreen } from 'global/components/modal';
import { Loader } from 'global/styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import sendData from 'redux/actions/sendData';
import { userHasPermissions } from 'redux/actions/userLogin';
import { ASSESSMENTS_LIST_PATH } from './createFromTemplateConfig';
import { ACCESS_ALERT_QUERY, NO_CREATE_PERMISSION_ALERT_VALUE, TEMPLATE_NOT_FOUND_ALERT_VALUE } from 'global/utils/loginRedirect';
import { getOnlyTiersFromScorecards } from '../ReportFab';
import useTemplateLocationSteps, { defaultAssessmentName } from '../template-location-steps';

// Same include set the modal flow relies on (authors/editors/realm/scorecards),
// so onlyTiers can be computed from the template's scorecards and the picker can
// open to the template's realm.
const TEMPLATE_INCLUDE = 'authors,editors,realm,scorecards';

// PFM-2545: full-screen page behind the static /reports/create/:templateId link.
// It replicates the post-template-click modal: pick a location, and for a lot-tier
// location also set a name + required assignee, then create the assessment and land
// the user on it. Two failure cases bounce to the assessments list with a plain-text
// snackbar (no create permission; template gone / no access).
const AssessmentCreateFromTemplate = (props) => {
  const templateId = get(props, 'match.params.templateId');
  const realm = useSelector((state) => get(state, 'auth.realm'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const redirectedRef = useRef(false);

  // PFM-2720: read the permissions-loaded flag reactively so this component
  // re-renders when getUserPermissions() resolves. A static create-from-template
  // link opened while logged out redirects here immediately after login, before
  // App.js's getUserPermissions() has populated auth.permissions. Without waiting,
  // the gate below evaluates against the empty initial array and wrongly bounces
  // the user with "You do not have permission to create assessments".
  const permissionsLoaded = useSelector((state) => !!get(state, 'auth.permissionsLoaded'));

  // AC#4a: the create-permission gate runs in-page (NOT the generic PermissionGate,
  // which alert()s + bounces to /home). Missing permission => list + plain message.
  // Gate on permissionsLoaded so "still fetching" stays on the loader rather than
  // being treated as "no permission".
  const canCreate = permissionsLoaded && userHasPermissions('createReports');

  const redirectToList = (alertValue) => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    props.history.replace(`${ASSESSMENTS_LIST_PATH}?${ACCESS_ALERT_QUERY}=${alertValue}`);
  };

  useEffect(() => {
    // Only bounce for missing permission once we actually know the permissions;
    // while they're still loading, showLoader keeps a spinner up.
    if (permissionsLoaded && !canCreate) {
      redirectToList(NO_CREATE_PERMISSION_ALERT_VALUE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoaded, canCreate]);

  // Single-template fetch. Only fetch once we know the user can create (waitFor),
  // so a permission-denied user never triggers an extra request. transformData
  // collapses the collection to the single record.
  const template = useRetrieveData(
    { url: `/assessment-templates/${templateId}` },
    {
      useEffect: true,
      waitFor: canCreate ? templateId : null,
      build: ['assessments'],
      transformData: (d) => d[0],
      params: { include: TEMPLATE_INCLUDE },
    }
  );

  const temp = template.data;

  // PFM-2722: templates are soft-deleted, so a direct by-id read still returns the
  // record (by design, so it can be restored). The API flags that state with a
  // deleted-at attribute (camelized to deletedAt) on direct reads only, so a stale
  // copied link must be treated the same as template-not-found.
  const templateDeleted = !!get(temp, 'deletedAt');

  // AC#4b: template fetch error / empty / soft-deleted (or not associated) => list + message.
  useEffect(() => {
    if (!canCreate) return;
    if (template.loading) return;
    const hasData = !!temp && size(Object.keys(temp)) > 0;
    if (template.error || !hasData || templateDeleted) {
      redirectToList(TEMPLATE_NOT_FOUND_ALERT_VALUE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreate, template.loading, template.error, temp, templateDeleted]);

  const onlyTiers = (() => {
    if (!temp) return ['division', 'community', 'lot'];
    return getOnlyTiersFromScorecards(get(temp, 'scorecards', []));
  })();

  // POST /assessments from the template + selected location, then land on the new
  // assessment. The create payload mirrors ReportFab's from-template branch
  // (realm_id, name, template_id); the backend copies scorecards from the template.
  const createAssessment = async (r, values = {}) => {
    try {
      setLoading(true);
      setError();

      const relationships = {};
      const assignees = get(values, 'assignees');
      if (assignees) {
        const arr = Array.isArray(assignees) ? assignees : [assignees];
        set(relationships, 'assignees', {
          data: arr.filter(Boolean).map((u) => ({ type: 'users', id: get(u, 'id') })),
        });
      }

      const data = {
        data: {
          type: 'assessments',
          attributes: {
            realm_id: get(r, 'id').toString(),
            name: get(values, 'reportName') || defaultAssessmentName(r, temp),
            template_id: get(temp, 'id'),
          },
          relationships,
        },
      };

      const resp = await sendData({ url: '/assessments', method: 'post', data });
      const newId = get(resp, 'data.data.id');
      // Lot/community land on scores; division opens to info (same as the modal flow).
      const tab = get(r, 'tier') === 'division' ? 'info' : 'scores';
      // ABSOLUTE path: from /reports/create/:id a relative push would resolve wrong.
      props.history.replace(`/reports/assessment/${newId}/${tab}`);
    } catch (err) {
      setLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const handleClose = () => {
    props.history.replace(ASSESSMENTS_LIST_PATH);
  };

  const { content: locationContent, buttons: locationButtons } = useTemplateLocationSteps({
    template: temp && !templateDeleted ? temp : undefined,
    currentRealm: realm,
    onlyTiers,
    onCancel: handleClose,
    onSubmit: createAssessment,
    disabled: loading,
  });

  // While we resolve permission / template, render the shell with a loader. The
  // redirect effects above take over the moment either check fails. A soft-deleted
  // template stays on the loader too so the location picker never flashes pre-redirect.
  const showLoader = !canCreate || template.loading || loading || !temp || templateDeleted;

  return (
    <FullScreen
      open
      onClose={handleClose}
      closeIcon="close"
      title={temp && !templateDeleted ? `New ${get(temp, 'name', 'Assessment')}` : 'Create Assessment'}
      centerContentStyle={{ padding: 0 }}
      buttons={!showLoader ? locationButtons : undefined}
    >
      <div style={{ width: '100%', height: '100%' }}>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setError()}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {showLoader && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', minHeight: 240 }}>
            <Loader />
          </div>
        )}

        {!showLoader && locationContent}
      </div>
    </FullScreen>
  );
};

export default withRouter(AssessmentCreateFromTemplate);
